import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MapPin, Star, Wrench } from "lucide-react";
import { toast } from "sonner";
import { REPAIR_STATUS, StatusChip, Thread } from "@/components/common/Thread";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useHousehold } from "@/context/HouseholdContext";
import { CATEGORY_LABELS, useAssets } from "@/hooks/use-assets";
import { useHouseholdDetail } from "@/hooks/use-household-detail";
import { api, formatApiError } from "@/lib/api";
import { useFormat } from "@/lib/format";

export const useNetwork = (type) => {
  const { active } = useHousehold();
  return useQuery({ queryKey: ["network", active?.id, type], queryFn: () => api.get(`/households/${active.id}/network/${type}`).then((r) => r.data), enabled: !!active });
};
export const useRepairs = () => {
  const { active } = useHousehold();
  return useQuery({ queryKey: ["repairs", active?.id], queryFn: () => api.get(`/households/${active.id}/repairs`).then((r) => r.data), enabled: !!active });
};
const sameBrand = (o, a) => (o.brands || []).some((b) => b.toLowerCase() === (a?.brand || "").toLowerCase());

export const RepairDialog = ({ open, onOpenChange, assetId }) => {
  const { active } = useHousehold();
  const qc = useQueryClient();
  const { data: assets = [] } = useAssets();
  const { data: repairers = [] } = useNetwork("repairer");
  const { data: makers = [] } = useNetwork("manufacturer");
  const [v, setV] = useState({ asset_id: assetId || "", org_id: "", description: "" });
  const asset = assets.find((a) => a.id === v.asset_id);
  const pros = [...makers.filter((o) => sameBrand(o, asset)).map((o) => ({ ...o, tag: "SAV fabricant" })),
    ...repairers.map((o) => ({ ...o, tag: (o.specialties || []).includes(asset?.category) ? "Spécialiste" : null }))
      .sort((a, b) => (b.tag ? 1 : 0) - (a.tag ? 1 : 0))];
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/households/${active.id}/repairs`, v);
      ["repairs", "dashboard"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      toast.success("Demande de réparation envoyée");
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="repair-dialog">
        <DialogHeader><DialogTitle className="font-heading">Demander une réparation</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div><Label>Équipement</Label>
            <Select value={v.asset_id} onValueChange={(x) => setV({ ...v, asset_id: x, org_id: "" })}>
              <SelectTrigger className="mt-1.5" data-testid="repair-asset-select"><SelectValue placeholder="Choisir" /></SelectTrigger>
              <SelectContent>{assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}{a.brand ? ` · ${a.brand}` : ""}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {asset && (
            <div className="space-y-2" data-testid="repair-pro-list">
              <Label>Professionnel</Label>
              {pros.map((o) => (
                <button type="button" key={o.id} onClick={() => setV({ ...v, org_id: o.id })} data-testid={`repair-pro-${o.id}`}
                  className={`w-full rounded-xl border p-3 text-left text-sm transition-colors ${v.org_id === o.id ? "border-primary bg-primary/5" : "border-border hover:bg-secondary"}`}>
                  <div className="flex items-center gap-2 font-semibold">{o.name}{o.tag && <span className="tone-chip tone-mint rounded-full px-2 text-[10px]">{o.tag}</span>}</div>
                  <div className="flex flex-wrap gap-x-3 text-xs text-muted-foreground">
                    {o.city && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{o.city}</span>}
                    {o.rating && <span className="inline-flex items-center gap-1"><Star className="h-3 w-3" />{o.rating}</span>}
                    <span>{o.description}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
          <div><Label htmlFor="r-desc">Décrivez la panne</Label><Textarea id="r-desc" className="mt-1.5" value={v.description} onChange={(e) => setV({ ...v, description: e.target.value })} required minLength={5} placeholder="Symptômes, code erreur, depuis quand…" data-testid="repair-description-input" /></div>
          <p className="text-xs text-muted-foreground">Le professionnel reçoit l'équipement concerné, votre description, votre nom et le nom du foyer. Ni votre adresse ni vos autres données.</p>
          <Button type="submit" className="w-full rounded-full" disabled={!v.asset_id || !v.org_id} data-testid="repair-submit-button">Envoyer la demande</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const RepairCard = ({ r, canEdit }) => {
  const { active } = useHousehold();
  const qc = useQueryClient();
  const { money } = useFormat();
  const refresh = () => ["repairs", "dashboard"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const decide = async (action, msg) => { try { await api.post(`/households/${active.id}/repairs/${r.id}/decision`, { action }); toast.success(msg); refresh(); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <li className="card-surface space-y-3 p-4" data-testid={`repair-${r.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-xs text-muted-foreground">{r.ref}</span>
        <span className="min-w-0 flex-1 truncate font-semibold">{r.asset?.name} <span className="font-normal text-muted-foreground">· {r.org_name}</span></span>
        <StatusChip map={REPAIR_STATUS} status={r.status} testid={`repair-status-${r.id}`} />
      </div>
      <p className="text-sm text-muted-foreground">{r.description}</p>
      {r.quote && (
        <div className="tone-wash tone-butter flex flex-wrap items-center gap-3 rounded-xl p-3 text-sm" data-testid={`repair-quote-${r.id}`}>
          <span className="flex-1">Devis : <b className="num">{money(r.quote.amount, { maximumFractionDigits: 2 })}</b>{r.quote.visit_date && ` · intervention proposée le ${new Date(r.quote.visit_date).toLocaleDateString("fr-FR")}`}</span>
          {canEdit && r.status === "quoted" && <>
            <Button size="sm" className="rounded-full" onClick={() => decide("accept", "Devis accepté")} data-testid={`repair-accept-${r.id}`}>Accepter</Button>
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => decide("decline", "Devis refusé")} data-testid={`repair-decline-${r.id}`}>Refuser</Button>
          </>}
        </div>
      )}
      <Thread messages={r.messages} testid={`repair-thread-${r.id}`} onSend={canEdit ? async (text) => { await api.post(`/households/${active.id}/repairs/${r.id}/messages`, { text }); refresh(); } : null} />
      {canEdit && ["new", "quoted"].includes(r.status) && <button className="text-xs text-muted-foreground underline" onClick={() => decide("cancel", "Demande annulée")} data-testid={`repair-cancel-${r.id}`}>Annuler la demande</button>}
    </li>
  );
};

const Directory = () => {
  const { data = [] } = useNetwork("repairer");
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="repairers-directory">
      {data.map((o) => (
        <div key={o.id} className="card-surface tone-wash tone-sky p-4 text-sm" data-testid={`repairer-${o.id}`}>
          <div className="font-semibold">{o.name}</div>
          <div className="mt-1 flex gap-3 text-xs text-muted-foreground"><span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{o.city}</span><span className="inline-flex items-center gap-1"><Star className="h-3 w-3" />{o.rating}</span></div>
          <p className="mt-2 text-xs">{o.description}</p>
          <div className="mt-2 flex flex-wrap gap-1">{(o.specialties || []).map((s) => <span key={s} className="rounded-full bg-card px-2 py-0.5 text-[11px]">{CATEGORY_LABELS[s]}</span>)}</div>
        </div>
      ))}
    </div>
  );
};

export const RepairsPanel = () => {
  const { canEdit } = useHouseholdDetail();
  const { data = [] } = useRepairs();
  const [open, setOpen] = useState(false);
  return (
    <section className="space-y-6" data-testid="repairs-panel">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="flex-1 text-lg font-bold">Demandes de réparation (SAV)</h2>
        {canEdit && <Button className="rounded-full" onClick={() => setOpen(true)} data-testid="repair-new-button"><Wrench className="mr-2 h-4 w-4" />Nouvelle demande</Button>}
      </div>
      {data.length ? <ul className="space-y-3" data-testid="repairs-list">{data.map((r) => <RepairCard key={r.id} r={r} canEdit={canEdit} />)}</ul>
        : <p className="text-sm text-muted-foreground">Aucune demande pour l'instant.</p>}
      <div className="space-y-3"><h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Réparateurs partenaires</h3><Directory /></div>
      {open && <RepairDialog open onOpenChange={setOpen} />}
    </section>
  );
};
